import {
  ChevronUp,
  ExternalLink,
  Mail,
  MapPin,
  Phone,
} from "lucide-react";
import { Link } from "react-router-dom";

const ICON_STROKE = 1.5;

export function SiteFooter({ uni, logoSrc }) {
  const fc = uni.featuredCampus;

  return (
    <footer className="relative mt-auto overflow-hidden bg-ink-900 text-slate-300">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(159,18,57,0.2),transparent)]"
        aria-hidden
      />
      <div className="relative border-t border-white/10">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-maroon-500/50 to-transparent" />
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:py-14">
          <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-12 lg:gap-12">
            <div className="lg:col-span-5">
              <div className="flex items-start gap-4">
                <img
                  src={logoSrc}
                  alt=""
                  width={52}
                  height={52}
                  className="h-12 w-12 shrink-0 rounded-xl bg-white/10 object-contain p-1 ring-1 ring-white/15 sm:h-14 sm:w-14"
                />
                <div>
                  <p className="text-base font-semibold tracking-tight text-white sm:text-lg">
                    {uni.shortName}
                  </p>
                  <p className="mt-1 max-w-sm text-sm leading-relaxed text-slate-400">
                    {uni.tagline}
                  </p>
                  <p className="mt-4 flex items-start gap-2 text-sm text-slate-400">
                    <MapPin
                      className="mt-0.5 h-4 w-4 shrink-0 text-maroon-400"
                      strokeWidth={ICON_STROKE}
                      aria-hidden
                    />
                    <span>
                      {uni.footer.campus}
                      {uni.footer.addressLines?.length ? (
                        <span className="mt-1 block text-slate-500">
                          {uni.footer.addressLines.join(" · ")}
                        </span>
                      ) : null}
                    </span>
                  </p>
                  {fc ? (
                    <div className="mt-4 rounded-xl border border-white/10 bg-maroon-950/40 px-4 py-3 text-sm">
                      <p className="font-semibold text-maroon-100">{fc.name}</p>
                      <ul className="mt-2 space-y-1.5 text-slate-400">
                        {fc.landline ? (
                          <li className="flex items-center gap-2">
                            <Phone
                              className="h-3.5 w-3.5 shrink-0 text-maroon-400"
                              strokeWidth={ICON_STROKE}
                              aria-hidden
                            />
                            <span>Line {fc.landline}</span>
                          </li>
                        ) : null}
                        {fc.mobile ? (
                          <li className="flex items-center gap-2">
                            <Phone
                              className="h-3.5 w-3.5 shrink-0 text-maroon-400"
                              strokeWidth={ICON_STROKE}
                              aria-hidden
                            />
                            <a
                              href={`tel:${fc.mobile.replace(/\s/g, "")}`}
                              className="hover:text-white"
                            >
                              Mobile {fc.mobile}
                            </a>
                          </li>
                        ) : null}
                        {fc.locationLine ? (
                          <li className="flex items-start gap-2">
                            <MapPin
                              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-maroon-400"
                              strokeWidth={ICON_STROKE}
                              aria-hidden
                            />
                            <span>{fc.locationLine}</span>
                          </li>
                        ) : null}
                      </ul>
                    </div>
                  ) : null}
                  <a
                    href={uni.mainWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-maroon-400 hover:text-maroon-300"
                  >
                    {uni.mainWebsite.replace(/^https?:\/\//, "")}
                    <ExternalLink className="h-3.5 w-3.5" strokeWidth={ICON_STROKE} aria-hidden />
                  </a>
                </div>
              </div>
            </div>

            <nav className="lg:col-span-3" aria-label="Quick links">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                On this site
              </p>
              <ul className="mt-4 space-y-2.5">
                <li>
                  <Link
                    to="/"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Home
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#about"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    About &amp; vision
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#values"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Core values
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#programs"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Programs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#master-programs"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Master programs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#degree-programs-complete"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Degree listing (brochure)
                  </Link>
                </li>
                <li>
                  <Link
                    to="/#contact"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Contact
                  </Link>
                </li>
                <li>
                  <Link
                    to="/student-results"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Student results & transcript
                  </Link>
                </li>
                <li>
                  <Link
                    to="/apply"
                    className="group inline-flex items-center gap-1.5 text-sm text-slate-300 transition hover:text-white"
                  >
                    <span className="h-px w-3 bg-maroon-500/0 transition-all group-hover:w-4 group-hover:bg-maroon-400" />
                    Apply online
                  </Link>
                </li>
              </ul>
            </nav>

            <div className="lg:col-span-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Contact
              </p>
              <ul className="mt-4 space-y-3 text-sm">
                <li>
                  <a
                    href={`mailto:${uni.footer.email}`}
                    className="flex items-start gap-3 rounded-lg p-2 -m-2 text-slate-300 transition hover:text-white"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
                      <Mail
                        className="h-4 w-4 text-maroon-400"
                        strokeWidth={ICON_STROKE}
                        aria-hidden
                      />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-wide text-slate-500">Email</span>
                      <span className="break-all">{uni.footer.email}</span>
                    </span>
                  </a>
                </li>
                <li>
                  <a
                    href={`tel:${uni.footer.phone.replace(/\s/g, "")}`}
                    className="flex items-start gap-3 rounded-lg p-2 -m-2 text-slate-300 transition hover:text-white"
                  >
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
                      <Phone
                        className="h-4 w-4 text-maroon-400"
                        strokeWidth={ICON_STROKE}
                        aria-hidden
                      />
                    </span>
                    <span>
                      <span className="block text-xs uppercase tracking-wide text-slate-500">
                        Main phone
                      </span>
                      <span>{uni.footer.phone}</span>
                    </span>
                  </a>
                </li>
                {uni.footer.phoneAlt ? (
                  <li>
                    <a
                      href={`tel:${uni.footer.phoneAlt.replace(/\s/g, "")}`}
                      className="flex items-start gap-3 rounded-lg p-2 -m-2 text-slate-300 transition hover:text-white"
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/5 ring-1 ring-white/10">
                        <Phone
                          className="h-4 w-4 text-maroon-400"
                          strokeWidth={ICON_STROKE}
                          aria-hidden
                        />
                      </span>
                      <span>
                        <span className="block text-xs uppercase tracking-wide text-slate-500">
                          Alternate phone
                        </span>
                        <span>{uni.footer.phoneAlt}</span>
                      </span>
                    </a>
                  </li>
                ) : null}
              </ul>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 bg-black/25">
          <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-4 px-4 py-5 sm:flex-row sm:px-6">
            <p className="text-center text-xs leading-relaxed text-slate-500 sm:text-left">
              © {new Date().getFullYear()} {uni.shortName}.
            </p>
            <a
              href="#main"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400 transition hover:text-white"
            >
              <ChevronUp className="h-4 w-4" strokeWidth={ICON_STROKE} aria-hidden />
              Back to top
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
